import { useEffect, useState } from "react";

interface VerdictExample {
  action: string;
  target: string;
  steps: { label: string; status: "PASS" | "REVIEW" | "BLOCK" }[];
  verdict: "ALLOW" | "REVIEW" | "BLOCK";
  note: string;
  receipt: string;
}

const EXAMPLES: VerdictExample[] = [
  {
    action: "payments.transfer",
    target: "payments-api / eu-west-2",
    steps: [
      { label: "Identity verified", status: "PASS" },
      { label: "Scope authorised", status: "PASS" },
      { label: "Policy matched", status: "PASS" },
      { label: "Budget available", status: "PASS" },
    ],
    verdict: "ALLOW",
    note: "Within budget, scope, and policy — proceeds.",
    receipt: "rcpt_018f_7c2a",
  },
  {
    action: "infra.delete_volume",
    target: "infrastructure-api / production",
    steps: [
      { label: "Identity verified", status: "PASS" },
      { label: "Scope checked", status: "PASS" },
      { label: "Destructive-action policy", status: "BLOCK" },
    ],
    verdict: "BLOCK",
    note: "Destructive action outside policy — never reaches the target system.",
    receipt: "rcpt_018f_7c31",
  },
  {
    action: "customer.read_pii",
    target: "customer-data / restricted",
    steps: [
      { label: "Identity verified", status: "PASS" },
      { label: "Scope checked", status: "PASS" },
      { label: "Sensitive-data policy", status: "REVIEW" },
    ],
    verdict: "REVIEW",
    note: "Sensitive scope — routed to a human before it proceeds.",
    receipt: "rcpt_018f_7c44",
  },
];

const CYCLE_MS = 4200;

const LiveVerdictFeed = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % EXAMPLES.length);
    }, CYCLE_MS);
    return () => clearInterval(id);
  }, []);

  const example = EXAMPLES[index];

  return (
    <div className="airg-console font-mono text-xs">
      <div className="airg-console-toolbar">
        <div className="flex items-center gap-3">
          <span className="flex gap-1" aria-hidden="true">
            <span className="airg-console-dot" />
            <span className="airg-console-dot" />
            <span className="airg-console-dot" />
          </span>
          <span className="airg-console-title">AIRG / Runtime control</span>
        </div>
        <span className="airg-console-live">
          <span className="airg-console-live-dot" />
          Illustrative preview
        </span>
      </div>

      <div className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div
            key={`action-${index}`}
            className="ledger-reveal min-w-0"
          >
            <p className="airg-console-label">Incoming action</p>
            <p className="airg-console-action">
              <span className="airg-console-muted">agent.call / </span>
              {example.action}
            </p>
            <p className="airg-console-target">{example.target}</p>
          </div>

          <div className="airg-console-policy">
            <p className="airg-console-label">Policy set</p>
            <p>production.v4.2</p>
          </div>
        </div>

        <div className="airg-console-section">
          <div className="flex items-center justify-between mb-2">
            <p className="airg-console-label">Evaluation path</p>
            <span className="airg-console-muted">
              {example.steps.length} checks
            </span>
          </div>
          <div className="space-y-0">
            {example.steps.map((step, i) => (
              <div
                key={`${index}-${step.label}`}
                className="airg-console-check ledger-reveal"
                style={{ animationDelay: `${0.12 + i * 0.1}s` }}
              >
                <span className="airg-console-check-index">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{step.label}</span>
                <span className={`airg-console-status airg-console-status-${step.status.toLowerCase()}`}>
                  {step.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div
          key={`verdict-${index}`}
          className="airg-console-verdict ledger-reveal"
          style={{
            animationDelay: `${0.12 + example.steps.length * 0.1 + 0.1}s`,
          }}
        >
          <div>
            <p className="airg-console-label">Decision</p>
            <p className="airg-console-note">{example.note}</p>
          </div>
          <span className={`airg-console-decision airg-console-decision-${example.verdict.toLowerCase()}`}>
            {example.verdict}
          </span>
        </div>

        <div className="airg-console-footer">
          <span>Signed receipt / {example.receipt}</span>
          <span>Pre-execution</span>
        </div>
      </div>
    </div>
  );
};

export default LiveVerdictFeed;
